import React from 'react'
import { observer } from 'mobx-react'
import { BetterComponent } from '../better-component/better-component'
import { Tooltip } from 'antd'
import { cleanNumericDisplays } from '../../_utils/utils'
import numeral from 'numeral'
import { numberWithPercent } from '../../_utils/numeral'

interface IProps {
    purchases: number
    /**
     * Attributed purchases divided by clicks, expressed as a decimal fraction
     * (0.0413 renders as 4.13%). Null when the metrics source has no rate for
     * the row. Whether a rate exists at all is the caller's call, not this
     * component's — only the caller knows the attribution window the figure came
     * out of.
     */
    purchaseRateDecimal?: number | null
}

interface IState {}

/** Stands in wherever there is no figure worth putting a number on. */
const NO_VALUE_PLACEHOLDER = '--'

/**
 * Paired purchase cell: the attributed purchase count with its purchase rate
 * stacked underneath. The rate always occupies its own line so every row in the
 * column is the same height whether or not a rate can be shown.
 *
 * A zero count and a zero rate both render as the placeholder rather than as `0`
 * and `0.00%`, which is how a zero rate already reads elsewhere in reporting.
 */
@observer
export class PurchasesColumnView extends BetterComponent<IProps, IState> {
    public render() {
        const purchasesValue = this.renderPurchases(this.props.purchases)
        const purchasesExpanded = this.renderPurchases(this.props.purchases, '0,0')
        const isAbbreviated = purchasesValue !== purchasesExpanded

        return (
            <div className="purchases-col-view">
                {isAbbreviated ? (
                    <Tooltip trigger={'click'} title={purchasesExpanded}>
                        <span className="purchases-value cursor-pointer">{purchasesValue}</span>
                    </Tooltip>
                ) : (
                    <span className="purchases-value">{purchasesExpanded}</span>
                )}
                <span className="purchase-rate-value">{this.renderPurchaseRate()}</span>
            </div>
        )
    }

    /**
     * The rate is rendered exactly when the caller supplied a non-zero one.
     * Re-deciding here from a click count would contradict the caller for a
     * purchase attributed to an impression rather than a click, which is a real
     * rate measured over zero clicks.
     */
    private renderPurchaseRate(): string {
        const { purchaseRateDecimal } = this.props
        if (!purchaseRateDecimal) return NO_VALUE_PLACEHOLDER

        return numberWithPercent(purchaseRateDecimal)
    }

    private renderPurchases(purchases: number, format: string = '0.00a'): string {
        if (!purchases) return NO_VALUE_PLACEHOLDER

        const value =
            String(purchases).length > 4 ? numeral(purchases).format(format) : numeral(purchases).format('0,0')

        return cleanNumericDisplays(value)
    }
}
